import { useEffect, useRef } from 'react'
import { useTranslation, Trans } from 'next-i18next'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { RecurringNudgeText } from './recurring-nudge-step.styled'
import useFlow from 'modules/flow/state/use-flow'
import { trackFormSubmitEvent } from '../../../analytics/helpers/track-form-events'
import { LordIcon } from '@classy/campaign-page-blocks'
import styled from '@emotion/styled'
import { convertRgbOrRgbaToHex } from '../../../../utils/colors'
import { formatCurrency } from 'utils/functions/currencyUtils'
import { getCurrencyDecimalPlaces } from 'utils/currency'

const StyledFooterButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: var(--classy-color__neutral--30);
  font-size: 14px;
  line-height: 16px;
`

const RecurringNudgeStep = () => {
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const { t, i18n } = useTranslation()

  // Create a ref for the primary button
  const initialFocusRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (initialFocusRef.current) {
      initialFocusRef.current.focus()
    }
  }, [])

  const calculateMonthlySuggestion = () => {
    const amount = order?.amount?.value

    return Math.round(amount / 4)
  }

  const suggestedMonthlyAmount = Number(
    (order.currency.exchangeRate * calculateMonthlySuggestion()).toFixed(
      getCurrencyDecimalPlaces(order.currency.value),
    ),
  )

  const suggestedMonthlyAmountCurrency = formatCurrency(
    suggestedMonthlyAmount,
    order.currency.value,
    i18n.language,
    {
      maximumFractionDigits: 0,
    },
  )

  const goNext = () => {
    flow.nextStep && flow.goToStep(flow.nextStep.name)
  }

  const onMonthlyClick = () => {
    // Track form submission for monthly donation
    trackFormSubmitEvent('recurring-nudge', { nudge_accepted: true })

    order.frequency.setValue('monthly')
    order.amount.setValue(suggestedMonthlyAmount)

    goNext()
  }

  const onOnetimeClick = () => {
    // Track form submission for one-time donation
    trackFormSubmitEvent('recurring-nudge', { nudge_accepted: false })

    order.frequency.setValue('one-time')
    goNext()
  }

  return (
    <>
      <FlowTwoColumnStepLayout.Header
        title={
          <Trans i18nKey="recurringNudgeStep.header.title">
            Become a monthly <br />
            supporter!
          </Trans>
        }
        titleSize="md"
        backButtonDataId="recurring-nudge-step-back-button"
        marginTop="2em"
      />
      <FlowTwoColumnStepLayout.Body>
        <Column>
          <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LordIcon
              size={100}
              name={config.recurringNudge.iconName}
              animation={config.recurringNudge.iconAnimation}
              variant={config.recurringNudge.iconStyle}
              // check if the element has colors, if not load theme color
              primaryColor={
                config.recurringNudge.iconPrimaryColor ||
                convertRgbOrRgbaToHex(config.theme['--theme-color__brand-primary'] || '')
              }
              secondaryColor={
                config.recurringNudge.iconSecondaryColor ||
                convertRgbOrRgbaToHex(config.theme['--theme-color__brand-primary'] || '')
              }
            />
          </Row>
          <RecurringNudgeText>
            <Trans
              i18nKey="recurringNudgeStep.monthlySupportersPrompt"
              values={{
                suggestedMonthlyAmount: suggestedMonthlyAmountCurrency,
              }}
              defaults="Would you consider becoming one of our monthly supporters by switching to a<br />
              <s>
                {{suggestedMonthlyAmount}} monthly
              </s>
              donation?"
              components={{ s: <span style={{ fontWeight: 'bold' }} /> }}
            />
          </RecurringNudgeText>

          {/* TODO - i18n: Determine the appropriate translation for the
          config-based 'recurringNudge.appeal' */}
          <RecurringNudgeText>{config.recurringNudge.appeal}</RecurringNudgeText>
        </Column>
      </FlowTwoColumnStepLayout.Body>

      <FlowTwoColumnStepLayout.Footer>
        <FlowTwoColumnStepLayout.FooterButton
          onClick={onMonthlyClick}
          dataId="recurring-nudge-monthly-button"
          ref={initialFocusRef}
        >
          {t('recurringNudgeStep.footer.monthlyButton', 'Donate {{suggestedMonthlyAmount}}/month', {
            suggestedMonthlyAmount: suggestedMonthlyAmountCurrency,
          })}
        </FlowTwoColumnStepLayout.FooterButton>
        <StyledFooterButton onClick={onOnetimeClick}>
          {t('recurringNudgeStep.footer.keepOneTimeGift', 'Keep my one-time gift')}
        </StyledFooterButton>
      </FlowTwoColumnStepLayout.Footer>
    </>
  )
}

export default RecurringNudgeStep
